import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { MeetingService } from '../../services/meeting.service';
import { NotificationService } from '../../services/notification.service';
import { AgreementRemindersApiActions, AgreementRemindersPageActions, AgreementsApiActions, AgreementsPageActions, BoardHistoryApiActions, BoardHistoryPageActions, MeetingAccessApiActions, MeetingAccessPageActions, MeetingDetailsPageActions, MeetingHistoryApiActions, MeetingHistoryPageActions, MeetingsApiActions, MeetingsPageActions, MeetingsMutationActions, UpcomingMeetingsApiActions, UpcomingMeetingsPageActions } from './meetings.actions';

@Injectable()
export class MeetingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly meetingService = inject(MeetingService);
  private readonly notificationService = inject(NotificationService);

  readonly loadMeetingHistory$ = createEffect(() => this.actions$.pipe(
    ofType(MeetingHistoryPageActions.opened, MeetingHistoryPageActions.filtersChanged, MeetingHistoryPageActions.pageChanged),
    switchMap((action) => {
      const filters = 'filters' in action
        ? action.filters
        : { status: 'completed' as const, page: action.page, pageSize: action.pageSize };
      return this.meetingService.getAll(filters).pipe(
        map((response) => MeetingHistoryApiActions.loadSucceeded({ meetings: response.items ?? [], total: response.total ?? 0 })),
        catchError((error: { message?: string }) => of(MeetingHistoryApiActions.loadFailed({ error: error?.message ?? 'Unable to load meeting history' }))),
      );
    }),
  ));

  readonly loadMeetingAccess$ = createEffect(() => this.actions$.pipe(
    ofType(MeetingAccessPageActions.opened),
    switchMap(({ filters }) => this.meetingService.getAll(filters).pipe(
      map((response) => MeetingAccessApiActions.loadSucceeded({ meetings: response.items ?? [], total: response.total ?? 0 })),
      catchError((error: { message?: string }) => of(MeetingAccessApiActions.loadFailed({ error: error?.message ?? 'Unable to load meeting access' }))),
    )),
  ));

  readonly loadUpcomingMeetings$ = createEffect(() => this.actions$.pipe(
    ofType(UpcomingMeetingsPageActions.opened),
    switchMap(() => this.meetingService.getAll({ status: 'scheduled', pageSize: 10 }).pipe(
      map((response) => UpcomingMeetingsApiActions.loadSucceeded({ meetings: response.items ?? [], total: response.total ?? 0 })),
      catchError((error: { message?: string }) => of(UpcomingMeetingsApiActions.loadFailed({ error: error?.message ?? 'Unable to load upcoming meetings' }))),
    )),
  ));

  readonly loadBoardHistory$ = createEffect(() => this.actions$.pipe(
    ofType(BoardHistoryPageActions.opened),
    switchMap(() => this.meetingService.getAll({ status: 'completed', boardOnly: true, pageSize: 200 }).pipe(
      map((response) => BoardHistoryApiActions.loadSucceeded({ meetings: response.items ?? [], total: response.total ?? 0 })),
      catchError((error: { message?: string }) => of(BoardHistoryApiActions.loadFailed({ error: error?.message ?? 'Unable to load board history' }))),
    )),
  ));

  readonly loadAgreements$ = createEffect(() => this.actions$.pipe(
    ofType(AgreementsPageActions.opened, AgreementsPageActions.filtersChanged),
    switchMap(({ responsible, trackingStatus }) => this.meetingService.getAllAgreements(responsible, trackingStatus).pipe(
      map((response) => AgreementsApiActions.loadSucceeded({ agreements: response.items, total: response.total })),
      catchError((error: { message?: string }) => of(AgreementsApiActions.loadFailed({ error: error?.message ?? 'Unable to load agreements' }))),
    )),
  ));

  readonly loadAgreementReminders$ = createEffect(() => this.actions$.pipe(
    ofType(AgreementRemindersPageActions.opened, AgreementRemindersPageActions.filtersChanged),
    switchMap(({ urgency }) => this.meetingService.getReminders(urgency).pipe(
      map((response) => AgreementRemindersApiActions.loadSucceeded({ reminders: response.items, total: response.total })),
      catchError((error: { message?: string }) => of(AgreementRemindersApiActions.loadFailed({ error: error?.message ?? 'Unable to load agreement reminders' }))),
    )),
  ));

  // ---------------------------------------------------------------------------
  // Load collection
  // ---------------------------------------------------------------------------

  readonly loadMeetings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MeetingsPageActions.opened,
        MeetingsPageActions.filtersChanged,
        MeetingsPageActions.pageChanged,
      ),
      switchMap((action) => {
        const filters = 'filters' in action
          ? action.filters
          : { page: action.page, pageSize: action.pageSize };

        return this.meetingService.getAll(filters).pipe(
          map((response) => MeetingsApiActions.loadSucceeded({
            meetings: response.items ?? [],
            total: response.total ?? 0,
          })),
          catchError((error: { message?: string }) => of(
            MeetingsApiActions.loadFailed({ error: error?.message ?? 'Unable to load meetings' }),
          )),
        );
      }),
    ),
  );

  readonly loadMeetingDocuments$ = createEffect(() => this.actions$.pipe(
    ofType(MeetingDetailsPageActions.documentsOpened),
    switchMap(({ meetingId }) => this.meetingService.getDocuments(meetingId).pipe(
      map((documents) => MeetingsApiActions.documentsLoadSucceeded({ meetingId, documents })),
      catchError((error: { message?: string }) => of(MeetingsApiActions.documentsLoadFailed({ meetingId, error: error?.message ?? 'Unable to load meeting documents' }))),
    )),
  ));

  readonly loadMeetingMinutes$ = createEffect(() => this.actions$.pipe(
    ofType(MeetingDetailsPageActions.minutesOpened),
    switchMap(({ meetingId }) => this.meetingService.getMinutes(meetingId).pipe(
      map((minutes) => MeetingsApiActions.minutesLoadSucceeded({ meetingId, minutes })),
      catchError((error: { message?: string }) => of(MeetingsApiActions.minutesLoadFailed({ meetingId, error: error?.message ?? 'Unable to load meeting minutes' }))),
    )),
  ));

  readonly notifyLoadFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsApiActions.loadFailed),
      tap(() => this.notificationService.error('MEETINGS.ERROR_LOADING')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  readonly createMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsMutationActions.createRequested),
      switchMap(({ requestId, dto }) =>
        this.meetingService.create(dto).pipe(
          map((meeting) => MeetingsMutationActions.createSucceeded({ requestId, meeting })),
          catchError((error: { message?: string }) =>
            of(MeetingsMutationActions.createFailed({
              requestId,
              error: error?.message ?? 'Unable to create meeting',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyCreateSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.createSucceeded),
      tap(() => this.notificationService.success('MEETINGS.CREATE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyCreateFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.createFailed),
      tap(() => this.notificationService.error('MEETINGS.CREATE_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Update
  // ---------------------------------------------------------------------------

  readonly updateMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsMutationActions.updateRequested),
      switchMap(({ requestId, id, dto }) =>
        this.meetingService.update(id, dto).pipe(
          map((meeting) => MeetingsMutationActions.updateSucceeded({ requestId, meeting })),
          catchError((error: { message?: string }) =>
            of(MeetingsMutationActions.updateFailed({
              requestId,
              error: error?.message ?? 'Unable to update meeting',
            })),
          ),
        ),
      ),
    ),
  );

  readonly updateMeetingAccess$ = createEffect(() => this.actions$.pipe(
    ofType(MeetingsMutationActions.updateAccessRequested),
    switchMap(({ requestId, id, dto }) => this.meetingService.updateAccess(id, dto).pipe(
      map((meeting) => MeetingsMutationActions.updateAccessSucceeded({ requestId, meeting })),
      catchError((error: { message?: string }) => of(MeetingsMutationActions.updateAccessFailed({
        requestId, error: error?.message ?? 'Unable to update meeting access',
      }))),
    )),
  ));

  readonly updateAgreementStatus$ = createEffect(() => this.actions$.pipe(
    ofType(MeetingsMutationActions.updateAgreementStatusRequested),
    switchMap(({ requestId, meetingId, agreementIndex, dto }) => this.meetingService.updateAgreementTrackingStatus(meetingId, agreementIndex, dto).pipe(
      map((agreement) => MeetingsMutationActions.updateAgreementStatusSucceeded({ requestId, agreement })),
      catchError((error: { message?: string }) => of(MeetingsMutationActions.updateAgreementStatusFailed({ requestId, error: error?.message ?? 'Unable to update agreement status' }))),
    )),
  ));

  readonly sendAgreementReminder$ = createEffect(() => this.actions$.pipe(
    ofType(MeetingsMutationActions.sendAgreementReminderRequested),
    switchMap(({ requestId, meetingId, agreementIndex, dto }) => this.meetingService.sendReminder(meetingId, agreementIndex, dto).pipe(
      map(() => MeetingsMutationActions.sendAgreementReminderSucceeded({ requestId, meetingId, agreementIndex })),
      catchError((error: { message?: string }) => of(MeetingsMutationActions.sendAgreementReminderFailed({ requestId, error: error?.message ?? 'Unable to send agreement reminder' }))),
    )),
  ));

  readonly notifyUpdateSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.updateSucceeded),
      tap(() => this.notificationService.success('MEETINGS.UPDATE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyUpdateFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.updateFailed),
      tap(() => this.notificationService.error('MEETINGS.UPDATE_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Update Agenda
  // ---------------------------------------------------------------------------

  readonly updateAgenda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsMutationActions.updateAgendaRequested),
      switchMap(({ requestId, id, dto }) =>
        this.meetingService.updateAgenda(id, dto).pipe(
          map((meeting) => MeetingsMutationActions.updateAgendaSucceeded({ requestId, meeting })),
          catchError((error: { message?: string }) =>
            of(MeetingsMutationActions.updateAgendaFailed({
              requestId,
              error: error?.message ?? 'Unable to update agenda',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyUpdateAgendaSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.updateAgendaSucceeded),
      tap(() => this.notificationService.success('MEETINGS.AGENDA.SAVE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyUpdateAgendaFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.updateAgendaFailed),
      tap(() => this.notificationService.error('MEETINGS.AGENDA.SAVE_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Update Attendance
  // ---------------------------------------------------------------------------

  readonly updateAttendance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsMutationActions.updateAttendanceRequested),
      switchMap(({ requestId, meetingId, email, dto }) =>
        this.meetingService.updateAttendance(meetingId, email, dto).pipe(
          map((meeting) => MeetingsMutationActions.updateAttendanceSucceeded({ requestId, meeting })),
          catchError((error: { message?: string }) =>
            of(MeetingsMutationActions.updateAttendanceFailed({
              requestId,
              error: error?.message ?? 'Unable to update attendance',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyUpdateAttendanceSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.updateAttendanceSucceeded),
      tap(() => this.notificationService.success('MEETINGS.ATTENDANCE.UPDATE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyUpdateAttendanceFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.updateAttendanceFailed),
      tap(() => this.notificationService.error('MEETINGS.ATTENDANCE.UPDATE_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Add Document
  // ---------------------------------------------------------------------------

  readonly addDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsMutationActions.addDocumentRequested),
      switchMap(({ requestId, id, dto }) =>
        this.meetingService.addDocument(id, dto).pipe(
          // After uploading, fetch the updated meeting to ensure documents are in sync
          switchMap(() =>
            this.meetingService.getById(id).pipe(
              map((meeting) => MeetingsMutationActions.addDocumentSucceeded({ requestId, meeting })),
              catchError((error: { message?: string }) =>
                of(MeetingsMutationActions.addDocumentFailed({
                  requestId,
                  error: error?.message ?? 'Unable to add document',
                })),
              ),
            ),
          ),
          catchError((error: { message?: string }) =>
            of(MeetingsMutationActions.addDocumentFailed({
              requestId,
              error: error?.message ?? 'Unable to add document',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyAddDocumentSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.addDocumentSucceeded),
      tap(() => this.notificationService.success('MEETINGS.DOCUMENTS.ADD_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyAddDocumentFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.addDocumentFailed),
      tap(() => this.notificationService.error('MEETINGS.DOCUMENTS.ADD_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Remove Document
  // ---------------------------------------------------------------------------

  readonly removeDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsMutationActions.removeDocumentRequested),
      switchMap(({ requestId, id, docId }) =>
        this.meetingService.removeDocument(id, docId).pipe(
          // After removing, fetch the updated meeting to ensure documents are in sync
          switchMap(() =>
            this.meetingService.getById(id).pipe(
              map((meeting) => MeetingsMutationActions.removeDocumentSucceeded({ requestId, meeting })),
              catchError((error: { message?: string }) =>
                of(MeetingsMutationActions.removeDocumentFailed({
                  requestId,
                  error: error?.message ?? 'Unable to remove document',
                })),
              ),
            ),
          ),
          catchError((error: { message?: string }) =>
            of(MeetingsMutationActions.removeDocumentFailed({
              requestId,
              error: error?.message ?? 'Unable to remove document',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyRemoveDocumentSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.removeDocumentSucceeded),
      tap(() => this.notificationService.success('MEETINGS.DOCUMENTS.REMOVE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyRemoveDocumentFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.removeDocumentFailed),
      tap(() => this.notificationService.error('MEETINGS.DOCUMENTS.REMOVE_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Save Minutes
  // ---------------------------------------------------------------------------

  readonly saveMinutes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsMutationActions.saveMinutesRequested),
      switchMap(({ requestId, id, dto }) =>
        this.meetingService.saveMinutes(id, dto).pipe(
          map((meeting) => MeetingsMutationActions.saveMinutesSucceeded({ requestId, meeting })),
          catchError((error: { message?: string }) =>
            of(MeetingsMutationActions.saveMinutesFailed({
              requestId,
              error: error?.message ?? 'Unable to save minutes',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifySaveMinutesSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.saveMinutesSucceeded),
      tap(() => this.notificationService.success('MEETINGS.MINUTES.SAVE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifySaveMinutesFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(MeetingsMutationActions.saveMinutesFailed),
      tap(() => this.notificationService.error('MEETINGS.MINUTES.SAVE_ERROR')),
    ),
    { dispatch: false },
  );
}
